import React from 'react'
import { ExpandMoreIcon } from '../../../lib/components'
import {
  CmsSectionTextColorProvider,
  getCmsSectionShellClassName,
  getCmsSectionShellStyle,
} from './cms-section-text-content'
import { useOpportunitySectionRegistration } from '../opportunity-section-navigation-context'

export type OpportunityExpandableSectionProps = {
  id: string
  title: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
  /** Matches CMSSectionShell / CMS section `data-testid` for tests and analytics */
  sectionTestId?: string
  backgroundColor?: string
  /** When false, section is omitted from programmatic nav expand handling */
  enableSectionNavigation?: boolean
}

const OpportunityExpandableSection = ({
  id,
  title,
  isOpen,
  onToggle,
  children,
  sectionTestId,
  backgroundColor,
  enableSectionNavigation = true,
}: OpportunityExpandableSectionProps) => {
  const panelId = `${id}-panel`

  useOpportunitySectionRegistration(
    id,
    isOpen,
    () => {
      if (!isOpen) {
        onToggle()
      }
    },
    enableSectionNavigation,
  )

  return (
    <section
      id={id}
      className={getCmsSectionShellClassName(
        'opportunity-overview-panel opportunity-cms-section-card opportunity-expandable-section',
        backgroundColor,
      )}
      style={getCmsSectionShellStyle(backgroundColor)}
      data-testid={sectionTestId}
    >
      <button
        type='button'
        className='opportunity-expandable-section__trigger'
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        data-testid={sectionTestId ? `${sectionTestId}-expand-trigger` : undefined}
      >
        <span
          id={`${id}-title`}
          className='opportunity-expandable-section__title opportunity-cms-section-title h5-heading'
        >
          {title}
        </span>
        <span
          className={`opportunity-expandable-section__chevron${
            isOpen ? ' opportunity-expandable-section__chevron--open' : ''
          }`}
          aria-hidden
        >
          <ExpandMoreIcon width='12' height='9' />
        </span>
      </button>
      {isOpen ? (
        <CmsSectionTextColorProvider backgroundColor={backgroundColor}>
          <div
            id={panelId}
            role='region'
            aria-labelledby={`${id}-title`}
            className='opportunity-expandable-section__body opportunity-cms-section-body'
          >
            {children}
          </div>
        </CmsSectionTextColorProvider>
      ) : null}
    </section>
  )
}

export { OpportunityExpandableSection }
